// #region Imports
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormArray } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalRef, ModalService, UploadedFileStatus } from '@vinnova/ui';
import { produce } from 'immer';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { v7 as uuid } from 'uuid';
import { getHandlingstypName } from '../../../core/models/form-settings.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  QuestionStructureSignering,
  SigneringType,
  SigneringTypeEnum,
} from '../../../core/models/question/question-signering.model';
import { QuestionStructureName } from '../../../core/models/question/question-structure.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../core/repositories/renderer-forms.repository';
import {
  BaseFileService,
  FormUploadedFile,
  FormUploadedSigneringFile,
} from '../../../core/services/base-file.service';
import { EditQuestionService } from '../../../core/services/edit-question.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { ReportService } from '../../../core/services/report.service';
import {
  EgreementSigneringStatus,
  EgreementStatesEnum,
  PPGResonseDataKeys,
  PPGResponseData,
  SigneringService,
  SigneringServiceError,
  SigneringServiceErrorEnum,
} from '../../../core/services/signering.service';

// #endregion Imports

@UntilDestroy()
@Component({
  selector: 'lib-question-signering',
  templateUrl: './question-signering.component.html',
  styleUrls: ['./question-signering.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionSigneringComponent implements OnInit, OnChanges {
  allowedFileTypes = input<string>();
  labelPrefix = input<string>();
  valueFormControl = input<FormArray<FormUploadedSigneringFile>>();
  question = input.required<Question>();
  repeaterGroupId = input<string>();

  /* isInEditQuestionMode should be set to true when viewed in edit-question-mode in Formulärbyggaren. */
  isInEditQuestionMode = signal<boolean>(false);
  isInFormBuilderMode = signal<boolean>(false);
  direktSigneringIsOpen = signal(false);
  manuellSigneringIsOpen = signal(false);
  isInvalid: WritableSignal<boolean> = signal(false);
  error: WritableSignal<boolean> = signal(false);
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  fileUploaded: boolean;
  fileList: FormUploadedSigneringFile[] = [];
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  isLoadingSigneringService$: Observable<boolean> = of(false);
  signeringServiceError$: Observable<SigneringServiceError[]> = of([]);
  fileToBeRemoved = signal<FormUploadedFile | null>(undefined);
  rapportId: string;

  /* PPG MVP */
  isMVP = signal<boolean>(false);
  isEnPartsProjekt = signal<boolean>(false);

  readonly ReadonlyState = ReadonlyState;
  readonly SigneringTypeEnum = SigneringTypeEnum;
  readonly SigneringServiceErrorEnum = SigneringServiceErrorEnum;
  readonly UploadedFileStatus = UploadedFileStatus;

  math = Math;

  removeSigneringModalRef: ModalRef;
  @ViewChild('removeSigneringModal') removeSigneringModal;

  // #region Getters
  get maxNumberOfFiles() {
    return this.question().validation.maxLength;
  }

  get required() {
    return this.question().validation.required;
  }

  get maxNumberOfPages() {
    return (this.question().structure as QuestionStructureSignering)
      .maxNumberOfPages;
  }

  get maxFileSize() {
    return +(this.question().structure as QuestionStructureSignering)
      .maxFileSize;
  }
  get structure() {
    return this.question().structure as QuestionStructureSignering;
  }
  get translations() {
    return this.question().translations?.list || {};
  }

  get questionStructureName() {
    return QuestionStructureName;
  }
  get manuellSigneringHandlingstyp(): string {
    return (this.question().structure as QuestionStructureSignering)
      .manuellSigneringHandlingstyp;
  }
  get uploadedFiles(): FormUploadedFile[] {
    return this.valueFormControl().value ?? [];
  }
  get isMaxNumberOfFilesReached(): boolean {
    return (
      this.maxNumberOfFiles &&
      this.maxNumberOfFiles <=
        this.fileList?.filter((f) =>
          [
            UploadedFileStatus.Uploaded,
            UploadedFileStatus.Loading,
            UploadedFileStatus.Success,
          ].includes(f.status),
        ).length
    );
  }
  get isUploadDisabled(): boolean {
    return this.isMaxNumberOfFilesReached;
  }
  get isDigitaltSignerad(): boolean {
    return this.fileList?.some(
      (f: FormUploadedSigneringFile) => f.digitaltSignerad,
    );
  }

  get isUpdatingAnswer() {
    return this.rendererFormRepository.getLoading();
  }

  // #endregion Getters

  constructor(
    private formValidationService: FormValidationService,
    private fileService: BaseFileService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private formQuery: RendererFormsQuery,
    private rendererFormRepository: RendererFormsRepository,
    private editQuestionService: EditQuestionService,
    private reportService: ReportService,
    private modalService: ModalService,
    private signeringService: SigneringService,
    private routes: ActivatedRoute,
  ) {
    effect(() => {
      this.fileList =
        (this.valueFormControl().value as FormUploadedSigneringFile[]) ?? [];
    });
  }

  ngOnInit(): void {
    this.rapportId = this.routes.snapshot.params.id;

    this.readOnly$ = this.formQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
    this.formValidationService
      .questionInvalid(this.valueFormControl(), this.question().id)
      .pipe(untilDestroyed(this))
      .subscribe((val) => {
        if (
          !this.fileUploaded &&
          this.routes.snapshot.params.queryParams?.EgreementStatus ===
            EgreementSigneringStatus.Success
        ) {
          this.isInvalid.set(false);
        } else if (
          this.fileUploaded ||
          this.valueFormControl().errors?.filePages
        ) {
          this.isInvalid.set(false);
        } else {
          this.isInvalid.set(val);
        }
      });

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.valueFormControl(),
      this.question(),
    );

    this.fileList =
      (this.valueFormControl()?.value as FormUploadedSigneringFile[]) || [];
    this.cdr.markForCheck();

    // Both accordion-cards are open when in question edit mode in formbuilder.
    this.editQuestionService.editingQuestionIds$
      .pipe(untilDestroyed(this))
      .subscribe((editingQuestionIds) => {
        this.isInEditQuestionMode.set(
          editingQuestionIds.includes(this.question().id),
        );
      });

    this.isInFormBuilderMode.set(
      this.routes.snapshot.parent.url[0].path === 'preview' ||
        this.routes.snapshot.parent.url[0].path === 'edit-form' ||
        this.routes.snapshot.parent.url[0].path === 'edit-question' ||
        this.routes.snapshot.parent.url[0].path === 'form-settings',
    );

    this.initMvpState();
    this.initCardToggleState();
    this.setupSigneringService();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      // registers and listens on changes
      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.valueFormControl(),
          {
            questionId: this.question().id,
            repeaterGroupId: this.repeaterGroupId(),
          },
          this.question(),
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.rendererFormRepository.setLoading(false);
        });

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(
          this.question(),
          this.repeaterGroupId(),
          this.valueFormControl(),
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.fileList =
            (this.valueFormControl().value as FormUploadedSigneringFile[]) ??
            [];
          this.cdr.markForCheck();
        });
    }
  }

  goToSignering(): void {
    const createSigneringCommand = {
      rapportId: this.routes.snapshot.params.id,
      language: this.translocoService.getActiveLang(),
    };
    this.signeringService.createSignering(createSigneringCommand);
  }

  openSignering(type: SigneringType): void {
    if (this.isInEditQuestionMode()) {
      return;
    }
    this.direktSigneringIsOpen.set(type === SigneringTypeEnum.DirektSignering);
    this.manuellSigneringIsOpen.set(
      type === SigneringTypeEnum.ManuellSignering,
    );
  }

  fileWindow(isOpen: boolean): void {
    this.error.set(false);
    if (
      !isOpen &&
      !this.fileList.some((f) => f.status === UploadedFileStatus.Loading)
    ) {
      this.valueFormControl().markAsTouched();
      this.valueFormControl().markAsDirty();
      if (this.question().validation.required && this.fileList.length === 0) {
        this.error.set(true);
      }
    }
    this.cdr.detectChanges();
  }

  // Återanvänd kod från file-upload.component.ts och anpassad för signering
  // Borde kunna brytas ut till en gemensam service
  uploadFiles(files: File[] | PPGResponseData): void {
    if (this.isUploadDisabled) {
      return;
    }
    const numberOfFiles = this.uploadedFiles.length;
    if (
      this.maxNumberOfFiles &&
      (numberOfFiles + (files as File[]).length > this.maxNumberOfFiles ||
        numberOfFiles + (files as PPGResponseData).ppgFiler.length >
          this.maxNumberOfFiles)
    ) {
      return;
    }

    const isDigitaltSignerad = files && PPGResonseDataKeys.ppgFiler in files;
    const filesToUpload: File[] = isDigitaltSignerad
      ? (files as PPGResponseData).ppgFiler || []
      : (files as File[]);

    for (const file of this.fileList) {
      if (
        !this.uploadedFiles.some((uf) => uf.uploadId === file.uploadId) &&
        file.status === UploadedFileStatus.Error
      ) {
        this.removeFileFromList(file.uploadId);
      }
    }
    this.rendererFormRepository.setLoading(true);
    // för att ladda flera filer
    // gör en array med observables och anropa apiet
    from(filesToUpload)
      .pipe(
        mergeMap((f) => {
          const file = new File([f], this.convertExtensionToLowercase(f.name));
          const uploadId = this.addNewFileToList(file);
          if (
            !file.name.endsWith('.doc') &&
            !file.name.endsWith('.docx') &&
            !file.name.endsWith('.txt') &&
            !file.name.endsWith('.xls') &&
            !file.name.endsWith('.xlsx') &&
            !file.name.endsWith('.pdf') &&
            !file.name.endsWith('.rtf')
          ) {
            const index = this.getFileIndex(uploadId);
            const fileList = [...this.fileList];
            fileList[index].status = UploadedFileStatus.Error;
            fileList[index].validationMessageKey =
              'fileupload_validation_wrong_filetype';
            this.fileList = fileList;
            this.rendererFormRepository.setLoading(false);
            return EMPTY;
          }

          const megaBytes = file.size / 1024 / 1024;
          if (this.maxFileSize != undefined && megaBytes > this.maxFileSize) {
            const index = this.getFileIndex(uploadId);
            const fileList = [...this.fileList];
            fileList[index].status = UploadedFileStatus.Error;
            fileList[index].validationMessageKey =
              'fileupload_validation_max_size_exceeded';
            fileList[index].validationMessageParams = {
              size: this.maxFileSize,
            };
            this.fileList = fileList;
            this.rendererFormRepository.setLoading(false);
            return EMPTY;
          }

          let fileName = '';
          if (isDigitaltSignerad) {
            fileName = (files as PPGResponseData).filnamn;
          } else {
            fileName =
              !this.maxNumberOfFiles || this.maxNumberOfFiles > 1
                ? `${getHandlingstypName(+this.manuellSigneringHandlingstyp)}_${this.getFileSuffix()}`
                : `${getHandlingstypName(+this.manuellSigneringHandlingstyp)}`;
          }
          this.addFileNameToList(uploadId, fileName);

          return this.fileService.uploadFile(file, fileName).pipe(
            map((file) => {
              const status = UploadedFileStatus.Success;

              let uploadedSigneringFile = {
                ...file,
                uploadId,
                handlingstyp: this.manuellSigneringHandlingstyp,
                validationMessageKey: 'fileupload_validation_maxpages',
                validationMessageParams: {
                  pages: file.pages,
                  maxpages: this.maxNumberOfPages,
                },
                status: status,
                rubrik: 'Projektparts godkännande',
                rubrikEN: 'Project agreement acceptance',
                beskrivning: 'Projektparts godkännande',
                beskrivningEN: 'Project agreement acceptance',
              } as FormUploadedSigneringFile;

              if (isDigitaltSignerad) {
                uploadedSigneringFile = {
                  ...uploadedSigneringFile,
                  projektpart: (files as PPGResponseData).signerandePart,
                  signeringstjanst: (files as PPGResponseData)
                    .signeringsTjanstNamn,
                  signeringstjanstId: (files as PPGResponseData).avtalId,
                  verifieringsURL: (files as PPGResponseData).verifyUrl,
                  signeradDatumTid: (files as PPGResponseData)
                    .signeradAvAllaParterDatum,
                  signerareLista: (files as PPGResponseData).signerare.map(
                    (signer) => ({
                      firstName: signer.firstName,
                      lastName: signer.lastName,
                      email: signer.email,
                      signeradDatumTid: signer.signeradDatumTid,
                    }),
                  ),
                  digitaltSignerad: isDigitaltSignerad,
                };
              }

              return uploadedSigneringFile;
            }),
            catchError(() => {
              this.rendererFormRepository.setLoading(false);
              const index = this.getFileIndex(uploadId);
              const fileList = [...this.fileList];
              fileList[index].status = UploadedFileStatus.Error;
              fileList[index].validationMessageKey =
                'fileupload_validation_error';
              this.fileList = fileList;
              return EMPTY;
            }),
          );
        }),
        finalize(() => {
          // detta körs när alla filer har laddats upp
          this.fileList = this.fileList.map((f) =>
            f.status === UploadedFileStatus.Success
              ? { ...f, status: UploadedFileStatus.Uploaded }
              : f,
          );
          this.addToControl();
          this.rendererFormRepository.setLoading(false);
          this.error.set(false);
          this.valueFormControl().markAsTouched();
          this.cdr.markForCheck();
        }),
        untilDestroyed(this),
      )
      .subscribe((result: FormUploadedSigneringFile) => {
        this.fileUploaded = true;
        const index = this.getFileIndex(result.uploadId);
        const fileList: FormUploadedSigneringFile[] = [...this.fileList];
        fileList[index] = result as FormUploadedSigneringFile;
        this.error.set(false);
        this.fileList = fileList;
        this.valueFormControl().markAsDirty();
        this.signeringService.setIsLoading(false);
      });
  }

  downloadFile(file: FormUploadedFile): void {
    this.fileService
      .downloadFile(file.pdfFileId, file.pdfFileName)
      .pipe(
        take(1),
        finalize(() => {
          this.valueFormControl().markAsTouched();
        }),
      )
      .subscribe();
  }

  removeFile(file: FormUploadedFile): void {
    this.removeFileFromList(file.uploadId);
    if (this.question().validation.required && this.fileList.length === 0) {
      this.error.set(true);
    }
    this.fileService
      .deleteFile(file.pdfFileId)
      .pipe(
        take(1),
        finalize(() => {
          this.fileUploaded = false;
          this.signeringService.deleteSigneradDokument(this.rapportId);
          this.valueFormControl().markAsTouched();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  isFileUploadedSuccess(): boolean {
    return this.fileList.every((f) => f.status === UploadedFileStatus.Uploaded);
  }

  // #region Modal methods
  showRemoveSigneringModal(file: FormUploadedFile): void {
    this.fileToBeRemoved.set(file);
    this.removeSigneringModalRef = this.modalService.show(
      this.removeSigneringModal,
      {
        backdrop: 'static',
        keyboard: false,
      },
    );
  }
  confirmRemoveSignering(): void {
    this.removeFile(this.fileToBeRemoved());
    this.fileToBeRemoved.set(undefined);
    this.removeSigneringModalRef.hide();
  }
  cancelRemoveSignering(): void {
    this.fileToBeRemoved.set(undefined);
    this.removeSigneringModalRef.hide();
  }
  // #endregion Modal methods

  // #region private methods
  private removeFileFromList(uploadId: string) {
    this.fileList = this.fileList.filter((f) => f.uploadId !== uploadId);
    this.valueFormControl().markAsDirty();
    this.valueFormControl().markAsTouched();
    this.addToControl();
  }
  private addToControl() {
    const filtered = produce(this.fileList, (draft) => {
      return draft.filter(
        (f) =>
          f.status === UploadedFileStatus.Success ||
          f.status === UploadedFileStatus.Uploaded ||
          f.status === UploadedFileStatus.ToManyPages,
      );
    });
    this.valueFormControl().patchValue(filtered);
  }
  private addFileNameToList(uploadId: string, fileName: string) {
    const index = this.getFileIndex(uploadId);
    const fileList = [...this.fileList];
    fileList[index].pdfFileName = fileName;
    this.fileList = fileList;
  }
  private getFileSuffix() {
    const fileList = [...this.fileList];
    const sorted = fileList
      .filter((f) => f.pdfFileName?.includes('_'))
      .map((f) => {
        let fileName = f.pdfFileName;
        if (fileName.includes('.')) {
          fileName = fileName.split('.')[0];
        }
        const split = fileName.split('_');
        const suffixString = split.at(-1);
        return isNaN(suffixString as unknown as number)
          ? undefined
          : +suffixString;
      })
      .sort((a, b) => (a > b ? -1 : 1));
    return sorted.length === 0 ? '1' : (sorted[0] + 1).toString();
  }
  private getFileIndex(uploadId: string): number {
    return this.fileList.findIndex((file) => file.uploadId === uploadId);
  }
  private addNewFileToList(file: File): string {
    const newFile = {
      originalFileName: file.name,
      uploadId: uuid(),
      status: UploadedFileStatus.Loading,
    } as FormUploadedSigneringFile;
    const fileList = [...this.fileList, newFile];

    this.fileList = fileList;
    return newFile.uploadId;
  }
  private convertExtensionToLowercase(filename: string) {
    const extensionIndex = filename.lastIndexOf('.');

    if (extensionIndex === -1) {
      return filename;
    }

    const name = filename.slice(0, extensionIndex);
    const extension = filename.slice(extensionIndex + 1);

    return name + '.' + extension.toLowerCase();
  }
  private validateFilePages(maxNumberOfPages: number) {
    return (formArray: FormArray<FormUploadedFile>) => {
      if (formArray.value) {
        let valid = true;
        for (const file of formArray.value) {
          if (file.pages > maxNumberOfPages) {
            valid = false;
            break;
          }
        }
        return valid ? undefined : { filePages: true };
      }
    };
  }
  private initCardToggleState(): void {
    if (this.isInFormBuilderMode()) {
      this.direktSigneringIsOpen.set(true);
      this.manuellSigneringIsOpen.set(false);
    } else {
      const isEligableForDirektsignering =
        this.isMVP() && this.isEnPartsProjekt();
      this.direktSigneringIsOpen.set(isEligableForDirektsignering);
      this.manuellSigneringIsOpen.set(!isEligableForDirektsignering);
    }
  }
  private setupSigneringService(): void {
    if (!this.isInFormBuilderMode()) {
      this.isLoadingSigneringService$ = this.signeringService.isLoading$;
      this.signeringServiceError$ =
        this.signeringService.signeringServiceError$;
      this.signeringService.checkSigneringStatus(this.rapportId);

      // Lyssna på signeringstatus och ladda ner signerade dokument när de är tillgängliga
      this.signeringService.signeringStatusChange
        .pipe(
          untilDestroyed(this),
          filter((status) => !!status),
          switchMap(
            (status: EgreementStatesEnum): Observable<PPGResponseData> => {
              if (
                this.fileList.length === 0 &&
                status === EgreementStatesEnum.SIGNED_BY_ALL
              ) {
                return this.signeringService
                  .getSigneradDokument(this.rapportId)
                  .pipe(
                    untilDestroyed(this),
                    filter((response) => !!response), // Filter out undefined responses
                  );
              }
              return EMPTY;
            },
          ),
        )
        .subscribe((response: PPGResponseData): void => {
          this.uploadFiles(response);
        });
    }
  }
  private initMvpState(): void {
    if (this.isInFormBuilderMode()) {
      this.isMVP.set(true);
      this.isEnPartsProjekt.set(true);

      this.editQuestionService.showFlerpartsProjekt$
        .pipe(untilDestroyed(this))
        .subscribe((showFlerparts) => {
          if (showFlerparts) {
            this.isMVP.set(false);
            this.isEnPartsProjekt.set(false);
          } else {
            this.isMVP.set(true);
            this.isEnPartsProjekt.set(true);
          }
        });
    } else {
      this.reportService.isMVP.pipe(untilDestroyed(this)).subscribe((isMVP) => {
        this.isMVP.set(isMVP);
      });
      this.reportService.isEnPartsProjekt$
        .pipe(untilDestroyed(this))
        .subscribe((isEnParts) => {
          this.isEnPartsProjekt.set(isEnParts);
        });
    }
  }
  // #endregion private methods
}
